import { jsx } from 'react/jsx-runtime';
import { useContext, createContext, useState, useEffect, useMemo, useRef } from 'react';
import { isSearchBarValueEmpty } from '../utils/isSearchBarValueEmpty.js';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useMutationCheckListItemState } from '../../onboarding/mutations/useMutationCheckListItemState.js';
import { searchBarValueToFabricFilters } from '../utils/converters.js';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { useQueryResourceContextSearch } from '../../resources/queries/useQueryResourceContextSearch.js';
import '../../onboarding/onboarding.config.js';
import { FabricQueryMode } from '@fabric/woody-client';
import { getSimilarTo } from '../../resources/utils/getSimilarTo.js';
import { keepPreviousData } from '@tanstack/react-query';
import { isResourceDetailFolder } from '../../resources/utils/resourceDetailGuards.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { createLogger } from '@fabric-app/utils/logging';
import { useResourceSwitcher } from '../../resource-detail/contexts/resourcesSwitcher.js';
import { OnboardingCheckListActionId } from '@fabric-app/types/onboarding';

const defaultSearchBarValue = {
  query: "",
  filters: [],
  color: void 0,
  imageUrl: void 0,
  similarToResource: void 0
};
const defaultValues = {
  searchBarValue: defaultSearchBarValue,
  setSearchBarValue: () => {
  },
  viewMode: "List",
  setViewMode: () => {
  },
  selectedIndex: 0,
  setSelectedIndex: () => {
  },
  zoomLevel: 0.9,
  setZoomLevel: () => {
  },
  scrollPosition: 0,
  setScrollPosition: () => {
  },
  storedScrollPosition: null,
  setStoredScrollPosition: () => {
  },
  lastInteraction: 0,
  setLastInteraction: () => {
  }
};
const GlobalSearchContext = /* @__PURE__ */ createContext(defaultValues);
const useGlobalSearchSetValue = () => {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error("useSetSearchbarValue must be used within a GlobalSearchProvider");
  }
  return ctx.setSearchBarValue;
};
const logger = createLogger("GlobalSearchProvider");
const GlobalSearchProvider = ({ children }) => {
  const [searchBarValue, setSearchBarValue] = useState(defaultSearchBarValue);
  const [viewMode, setViewMode] = useState("Grid");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [storedScrollPosition, setStoredScrollPosition] = useState(null);
  const [lastInteraction, setLastInteraction] = useState(0);
  useEffect(() => {
    setLastInteraction(Date.now());
  }, [
    searchBarValue,
    viewMode,
    selectedIndex,
    zoomLevel,
    scrollPosition
  ]);
  const { isDesktopView } = useResponsive();
  useEffect(() => {
    if (!isDesktopView) {
      logger.info("Changed resolution, switching similarToResource to undefined");
      setSearchBarValue((s) => ({
        ...s,
        similarToResource: void 0
      }));
    }
  }, [
    isDesktopView
  ]);
  const value = useMemo(() => ({
    searchBarValue,
    setSearchBarValue,
    viewMode,
    setViewMode,
    selectedIndex,
    setSelectedIndex,
    zoomLevel,
    setZoomLevel,
    scrollPosition,
    setScrollPosition,
    storedScrollPosition,
    setStoredScrollPosition,
    lastInteraction,
    setLastInteraction
  }), [
    searchBarValue,
    setSearchBarValue,
    viewMode,
    selectedIndex,
    zoomLevel,
    scrollPosition,
    storedScrollPosition,
    lastInteraction
  ]);
  return /* @__PURE__ */ jsx(GlobalSearchContext.Provider, {
    value,
    children
  });
};
const useGlobalSearchStore = ({ _id, viewportRef, disabled, searchArgs }) => {
  const { searchBarValue: _searchBarValue, setSearchBarValue, viewMode, setViewMode, selectedIndex, setSelectedIndex, zoomLevel, setZoomLevel, scrollPosition, setScrollPosition, storedScrollPosition, setStoredScrollPosition, lastInteraction, setLastInteraction } = useContext(GlobalSearchContext);
  const trimmedSearchBarValue = useMemo(() => ({
    query: _searchBarValue.query.trim(),
    filters: _searchBarValue.filters,
    imageUrl: _searchBarValue.imageUrl,
    color: _searchBarValue.color,
    similarToResource: _searchBarValue.similarToResource
  }), [
    _searchBarValue
  ]);
  const _isSearchBarEmpty = isSearchBarValueEmpty(trimmedSearchBarValue);
  const debouncedSearchBarValue = useDebounce(trimmedSearchBarValue, 200);
  const searchBarValue = _isSearchBarEmpty ? _searchBarValue : debouncedSearchBarValue;
  const isSearchBarEmpty = isSearchBarValueEmpty(searchBarValue);
  const [wasSearchBarEmpty, setWasSearchBarEmpty] = useState(isSearchBarEmpty);
  const { track } = useAnalytics();
  const storedQuery = useRef(searchBarValue.query);
  const { mutate: mutateCheckListItemState } = useMutationCheckListItemState();
  useEffect(() => {
    if (searchBarValue.query === storedQuery.current) return;
    const debounce = setTimeout(() => {
      storedQuery.current = searchBarValue.query;
      track(AnalyticsEvents.Search, {
        has_query: true,
        action: "global_search"
      });
      mutateCheckListItemState({
        actionId: OnboardingCheckListActionId.TRY_AI_SEARCH,
        state: true
      });
    }, 3e3);
    return () => clearTimeout(debounce);
  }, [
    mutateCheckListItemState,
    searchBarValue,
    track
  ]);
  const filters = useMemo(() => {
    return {
      ...searchBarValueToFabricFilters(searchBarValue)
    };
  }, [
    searchBarValue
  ]);
  const searchQueryArgs = useMemo(() => {
    const params = searchBarValue.imageUrl ? {
      mode: FabricQueryMode.SimilarImage,
      imageUrl: searchBarValue.imageUrl,
      filters
    } : searchBarValue.similarToResource ? {
      ...getSimilarTo(searchBarValue.similarToResource),
      filters
    } : {
      mode: FabricQueryMode.Hybrid,
      text: searchBarValue.query,
      filters
    };
    const options = {
      enabled: !disabled && !isSearchBarEmpty,
      placeholderData: isSearchBarEmpty || wasSearchBarEmpty ? void 0 : keepPreviousData
    };
    return {
      params,
      options
    };
  }, [
    searchBarValue,
    disabled,
    isSearchBarEmpty,
    wasSearchBarEmpty,
    filters
  ]);
  const { resources, fetchNextPage: _fetchNextPage, isFetchingNextPage, total, hasNextPage, isPlaceholderData, isFetching, isLoading: queryIsLoading, queryHash } = useQueryResourceContextSearch(searchBarValue.imageUrl ? {
    mode: FabricQueryMode.SimilarImage,
    imageUrl: searchBarValue.imageUrl,
    filters,
    ...searchArgs
  } : searchBarValue.similarToResource ? {
    ...getSimilarTo(searchBarValue.similarToResource),
    filters,
    ...searchArgs
  } : {
    mode: FabricQueryMode.Hybrid,
    text: searchBarValue.query,
    filters,
    ...searchArgs
  }, {
    enabled: !disabled && !isSearchBarEmpty,
    placeholderData: isSearchBarEmpty || wasSearchBarEmpty ? void 0 : keepPreviousData
  });
  const fetchNextPage = !isFetchingNextPage && hasNextPage ? _fetchNextPage : void 0;
  const isLoading = queryIsLoading || isPlaceholderData && isFetching;
  useEffect(() => {
    if (!isSearchBarEmpty) return;
    setWasSearchBarEmpty(true);
  }, [
    isSearchBarEmpty
  ]);
  const scrolledHash = useRef(void 0);
  useEffect(() => {
    if (isLoading || scrolledHash.current === queryHash || isSearchBarEmpty) return;
    setWasSearchBarEmpty(false);
    scrolledHash.current = queryHash;
    if (viewportRef) viewportRef.scrollTop = 0;
  }, [
    isLoading,
    queryHash,
    viewportRef,
    isSearchBarEmpty
  ]);
  const { setResourceIds: setSwitcherResourceIds } = useResourceSwitcher();
  useEffect(() => {
    if (disabled) return;
    setSwitcherResourceIds(resources.filter((item) => !isResourceDetailFolder(item)).map((f) => f.id), {
      contextId: "hybrid-search",
      /**
      * force update, we know that it can be overriden because if the (not)disabled condition is met
      */
      ignoreIfContextIdDoesntMatch: false,
      fetchNextPage
    });
  }, [
    disabled,
    resources,
    setSwitcherResourceIds,
    fetchNextPage
  ]);
  const onSearchKeyDown = (e) => {
    if (e.key === " " && selectedIndex !== null) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    e.stopPropagation();
    if (!resources.length) return;
    if (selectedIndex === null) {
      setSelectedIndex(e.key === "ArrowDown" ? 0 : resources.length - 1);
      return;
    }
    const newIndex = ((e.key === "ArrowDown" ? selectedIndex + 1 : selectedIndex - 1) + resources.length) % resources.length;
    setSelectedIndex(newIndex);
  };
  useEffect(() => {
    setSelectedIndex(null);
  }, [
    searchBarValue.query,
    setSelectedIndex
  ]);
  const { isMobileView } = useResponsive();
  useEffect(() => {
    if (!isMobileView) return;
    setViewMode("Grid");
  }, [
    isMobileView,
    setViewMode
  ]);
  const scrollPositionRef = useRef(scrollPosition);
  useEffect(() => {
    scrollPositionRef.current = scrollPosition;
  }, [
    scrollPosition
  ]);
  useEffect(() => {
    return () => {
      setStoredScrollPosition(scrollPositionRef.current);
    };
  }, [
    setStoredScrollPosition
  ]);
  useEffect(() => {
    if (!viewportRef || isLoading || storedScrollPosition === null) return;
    let lastTimeout;
    const scrollTo = () => {
      viewportRef.scrollTo({
        top: storedScrollPosition
      });
      lastTimeout = window.setTimeout(() => {
        if (viewportRef.scrollTop === storedScrollPosition) {
          setStoredScrollPosition(null);
        } else scrollTo();
      }, 100);
    };
    scrollTo();
    return () => clearInterval(lastTimeout);
  }, [
    isLoading,
    viewportRef,
    setStoredScrollPosition,
    storedScrollPosition
  ]);
  useEffect(() => {
    if (!lastInteraction || Date.now() - lastInteraction < 10 * 60 * 1e3) return;
    setSearchBarValue(defaultSearchBarValue);
    setSelectedIndex(null);
    setZoomLevel(0.9);
    setScrollPosition(0);
    setStoredScrollPosition(null);
    setLastInteraction(0);
  }, [
    lastInteraction,
    setSearchBarValue,
    setSelectedIndex,
    setZoomLevel,
    setScrollPosition,
    setStoredScrollPosition,
    setLastInteraction
  ]);
  return {
    hasNextPage,
    searchBarValue: _searchBarValue,
    setSearchBarValue,
    viewMode,
    setViewMode,
    resources,
    selectedIndex,
    setSelectedIndex,
    fetchNextPage,
    isFetchingNextPage,
    onSearchKeyDown,
    zoomLevel,
    setZoomLevel,
    storedScrollPosition,
    scrollPosition,
    setScrollPosition,
    isLoading,
    isSearchBarEmpty,
    total,
    queryHash,
    filters,
    searchQueryArgs
  };
};

export { GlobalSearchProvider, useGlobalSearchStore as default, defaultSearchBarValue, useGlobalSearchSetValue };
//# sourceMappingURL=hybridsearch.js.map
